import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingCart, Plus, Minus, Bell, X, Send, Check, Flame, Star, Sparkles, Tag, ChevronRight, ChevronLeft, ChevronsRight, ChevronsLeft, ImageIcon, Clock, MapPin, Phone, ChevronDown, Loader2, Hand, Search, ShoppingBag, ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import axios from 'axios';
import ItemDetailsDialog from '@/components/ItemDetailsDialog';
import MenuImage from '@/components/MenuImage';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useI18n, getLocalized } from '@/lib/i18n';
import { slugify } from '@/lib/slugify';
import { NutritionBadge } from '@/components/menu/NutritionBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientMenuPage({ domainMode = false } = {}) {
  const { tableCode, slug, tableNumber } = useParams();

  const isSlugMode = !!slug && !!tableNumber;
  const isDomainMode = !!domainMode && !!tableNumber && !slug;
  const storageKey = isDomainMode
    ? `order_dom_${window.location.host}_${tableNumber}`
    : isSlugMode
      ? `order_${slug}_${tableNumber}`
      : `order_${tableCode}`;

  const [data, setData] = useState(null);
  // Constrain the language switcher to languages the restaurant has enabled.
  const enabledLangs = data?.restaurant?.enabled_languages;
  const { lang, setLang, t } = useI18n(enabledLangs);
  const localizedName = (d) => getLocalized(d, 'name', lang);
  const localizedDescription = (d) => getLocalized(d, 'description', lang);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callingStaff, setCallingStaff] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.status === 'completed' || parsed.status === 'cancelled') return null;
        return parsed;
      } catch { return null; }
    }
    return null;
  });
  const [orderStatus, setOrderStatus] = useState(activeOrderId?.status || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [splashDismissed, setSplashDismissed] = useState(() => {
    return sessionStorage.getItem(`splash_${storageKey}`) === '1';
  });
  // Active splash chosen on first load (random pick from active list)
  const [activeSplash, setActiveSplash] = useState(null);
  // Item details modal
  const [detailsItem, setDetailsItem] = useState(null);

  // Category scroll state
  const categoryScrollRef = useRef(null);
  const headerRef = useRef(null);
  const categoryRefs = useRef({});
  const isProgrammaticScrollRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(true);

  const checkCategoryScroll = useCallback(() => {
    const el = categoryScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    // Hide hint after user scrolls
    if (el.scrollLeft > 10) setShowSwipeHint(false);
  }, []);

  useEffect(() => {
    const el = categoryScrollRef.current;
    if (!el) return;
    checkCategoryScroll();
    el.addEventListener('scroll', checkCategoryScroll, { passive: true });
    const ro = new ResizeObserver(checkCategoryScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkCategoryScroll);
      ro.disconnect();
    };
  }, [checkCategoryScroll, selectedSection, data]);

  const scrollCategories = (direction) => {
    const el = categoryScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 150, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchMenu = async () => {
      // Domain mode catches a bare /:tableNumber. Reject non-numeric values so
      // unrelated paths like "/admin" or "/login" don't trigger this route.
      if (isDomainMode && !/^\d+$/.test(String(tableNumber || ''))) {
        setError('Стол не найден');
        setLoading(false);
        return;
      }
      try {
        let response;
        if (isDomainMode) {
          response = await axios.get(`${API}/public/menu-by-domain/${tableNumber}?host=${encodeURIComponent(window.location.host)}`);
        } else if (isSlugMode) {
          response = await axios.get(`${API}/public/menu-by-slug/${slug}/${tableNumber}`);
        } else {
          response = await axios.get(`${API}/public/menu/${tableCode}`);
        }
        setData(response.data);
        
        const sections = response.data.sections || [];
        if (sections.length > 0) {
          setSelectedSection(sections[0].id);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Стол не найден');
        } else if (err.response?.status === 400) {
          setError(err.response.data.detail || 'Меню недоступно');
        } else {
          setError('Ошибка загрузки меню');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, [tableCode, slug, tableNumber, isSlugMode, isDomainMode]);

  // Poll order status
  useEffect(() => {
    if (!activeOrderId?.id) return;
    let active = true;
    const poll = async () => {
      try {
        const resp = await axios.get(`${API}/public/orders/${activeOrderId.id}/status`);
        if (!active) return;
        const newStatus = resp.data.status;
        setOrderStatus(newStatus);
        if (newStatus === 'completed' || newStatus === 'cancelled') {
          localStorage.removeItem(storageKey);
          setTimeout(() => { if (active) { setActiveOrderId(null); setOrderStatus(null); } }, 10000);
        }
      } catch { /* ignore */ }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => { active = false; clearInterval(interval); };
  }, [activeOrderId?.id, storageKey]);

  // Apply theme from settings
  useEffect(() => {
    if (data?.settings?.theme) {
      if (data.settings.theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
    
    // Cleanup - restore to light when leaving
    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, [data?.settings?.theme]);

  // Get categories for selected section
  const sectionCategories = data?.categories.filter(cat => cat.section_id === selectedSection) || [];
  const labelsMap = {};
  (data?.labels || []).forEach(l => { labelsMap[l.id] = l; });

  // Search helpers
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const matchesQuery = (item) => {
    if (!normalizedQuery) return true;
    const n = (item.name || '').toLowerCase();
    const d = (item.description || '').toLowerCase();
    return n.includes(normalizedQuery) || d.includes(normalizedQuery);
  };
  const getCategoryItems = (catId) =>
    (data?.items || []).filter(i => i.category_id === catId && matchesQuery(i));
  // Categories of current section that have at least one matching item
  const visibleCategories = sectionCategories.filter(cat => getCategoryItems(cat.id).length > 0);
  const totalMatches = visibleCategories.reduce((acc, cat) => acc + getCategoryItems(cat.id).length, 0);

  // Set first category when section changes
  useEffect(() => {
    if (sectionCategories.length > 0 && !sectionCategories.find(c => c.id === selectedCategory)) {
      setSelectedCategory(sectionCategories[0].id);
    }
  }, [selectedSection, sectionCategories]);

  // Scroll to top when switching sections (avoid staying mid-page on old content)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [selectedSection]);

  // Scroll to category section (with sticky header offset)
  const scrollToCategory = useCallback((catId) => {
    setSelectedCategory(catId);
    setShowSwipeHint(false);
    const el = categoryRefs.current[catId];
    if (!el) return;
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
    isProgrammaticScrollRef.current = true;
    window.scrollTo({ top, behavior: 'smooth' });
    window.clearTimeout(scrollToCategory._t);
    scrollToCategory._t = window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 700);
    // Note: pushState для новой категории делает единый debounce-эффект ниже,
    // так что клик по табу тоже попадает в историю (через 700ms).
  }, []);

  // ==== Deep-link support (anchor links to categories) ==========================
  // Supports both `?cat=<id-or-slug>` / `?category=<id-or-slug>` in the query
  // string and a `#slug` in the URL hash. Runs once when the menu data first
  // loads. If the category is in another section, that section is activated
  // first and the scroll happens shortly after so refs are already attached.
  const initialAnchorHandledRef = useRef(false);
  useEffect(() => {
    if (initialAnchorHandledRef.current) return;
    const cats = data?.categories || [];
    if (cats.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const rawAnchor = (
      params.get('cat') || params.get('category') || window.location.hash.replace(/^#/, '')
    ).trim();
    if (!rawAnchor) {
      initialAnchorHandledRef.current = true;
      return;
    }
    const normalized = slugify(decodeURIComponent(rawAnchor));
    const target = cats.find((c) => c.id === rawAnchor || slugify(c.name) === normalized);
    if (!target) {
      initialAnchorHandledRef.current = true;
      return;
    }
    initialAnchorHandledRef.current = true;
    if (target.section_id && target.section_id !== selectedSection) {
      setSelectedSection(target.section_id);
    }
    // Wait for the section switch + first render so categoryRefs are populated
    setTimeout(() => scrollToCategory(target.id), 250);
  }, [data?.categories]);

  // Синхронизация URL hash с активной категорией + история в браузере.
  // Пушим новую history entry с небольшой задержкой (~350мс) — этого достаточно,
  // чтобы очень быстрый свайп на 2-3 категории не создавал по entry на каждую,
  // но каждая категория, на которой юзер задержался, попадала в историю.
  // Таймер НЕ отменяется при смене категории (мы хотим последовательно пушнуть
  // A→B→C, даже если пользователь листает без остановки, чтобы «Назад» вернул
  // ровно по тому же маршруту).
  const suppressNextPushRef = useRef(false);
  useEffect(() => {
    if (!selectedCategory) return;
    const cats = data?.categories || [];
    const cat = cats.find((c) => c.id === selectedCategory);
    if (!cat) return;
    const slug = slugify(cat.name) || cat.id;
    const currentHash = window.location.hash.replace(/^#/, '');
    if (currentHash === slug) return;
    if (suppressNextPushRef.current) {
      suppressNextPushRef.current = false;
      return;
    }
    window.setTimeout(() => {
      // Перепроверяем — вдруг кто-то (popstate/другой effect) уже поставил такой hash.
      if (window.location.hash.replace(/^#/, '') === slug) return;
      try {
        const url = new URL(window.location.href);
        url.hash = slug;
        window.history.pushState(null, '', url);
      } catch { /* noop */ }
    }, 350);
  }, [selectedCategory, data?.categories]);

  // Кнопка «Наверх» — floating pill в правом нижнем углу.
  // Показываем после того, как пользователь пролистал > 400px.
  const [showTopBtn, setShowTopBtn] = useState(false);
  useEffect(() => {
    const on = () => setShowTopBtn(window.scrollY > 400);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);

  // Заголовок вкладки браузера: «Меню <название>» + favicon = логотип ресторана.
  // Инжектим и <link rel="icon">, и <link rel="apple-touch-icon"> — iOS Safari
  // использует именно apple-touch-icon для превью вкладки.
  useEffect(() => {
    const name = data?.restaurant?.name;
    const logo = data?.restaurant?.logo_url;
    if (!name) return;
    const prevTitle = document.title;
    document.title = `Меню ${name}`;

    const injected = [];
    const hidden = [];
    if (logo) {
      // Прячем существующие icon-links (не удаляем, чтобы восстановить при уходе).
      document
        .querySelectorAll(
          'link[rel="icon"], link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[rel="apple-touch-icon-precomposed"]'
        )
        .forEach((l) => {
          hidden.push({ el: l, parent: l.parentNode, next: l.nextSibling });
          l.parentNode.removeChild(l);
        });

      const mkLink = (rel) => {
        const el = document.createElement('link');
        el.rel = rel;
        el.href = logo;
        document.head.appendChild(el);
        injected.push(el);
      };
      mkLink('icon');
      mkLink('shortcut icon');
      mkLink('apple-touch-icon');
      mkLink('apple-touch-icon-precomposed');
    }

    return () => {
      document.title = prevTitle;
      injected.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      hidden.forEach(({ el, parent, next }) => {
        if (parent) parent.insertBefore(el, next);
      });
    };
  }, [data?.restaurant?.name, data?.restaurant?.logo_url]);

  // Back/Forward кнопка браузера — переключаем категорию согласно новому hash.
  // Ставим suppress-флаг, чтобы debounce-effect выше НЕ создал новую history entry
  // сразу после того, как браузер её удалил.
  useEffect(() => {
    const onPop = () => {
      const cats = data?.categories || [];
      if (!cats.length) return;
      const rawAnchor = window.location.hash.replace(/^#/, '').trim();
      if (!rawAnchor) return;
      const normalized = slugify(decodeURIComponent(rawAnchor));
      const target = cats.find((c) => c.id === rawAnchor || slugify(c.name) === normalized);
      if (!target) return;
      if (target.section_id && target.section_id !== selectedSection) {
        setSelectedSection(target.section_id);
      }
      suppressNextPushRef.current = true;
      setTimeout(() => scrollToCategory(target.id), 100);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [data?.categories, selectedSection, scrollToCategory]);
  // ==============================================================================

  // ScrollSpy — подсветка активной категории при прокрутке.
  // Реализация через scroll-listener вместо IntersectionObserver: для каждой
  // категории на каждом скролле берём `getBoundingClientRect().top` и выбираем
  // ту, чей заголовок ближе всего к нижней границе шапки СВЕРХУ (т.е.
  // только что проскроллен под шапку). Это переключает подсветку в
  // нужный момент — как только пользователь входит в новую категорию.
  useEffect(() => {
    if (!sectionCategories.length) return;

    let rafId = 0;
    const run = () => {
      rafId = 0;
      if (isProgrammaticScrollRef.current) return;
      const headerHeight = (headerRef.current?.offsetHeight || 0) + 24;
      let active = null;
      let bestTop = -Infinity;
      for (const cat of sectionCategories) {
        const el = categoryRefs.current[cat.id];
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        // Категория считается «активной», если её верх уже УШЁЛ под шапку
        // (top <= headerHeight). Среди таких выбираем ту, что ушла меньше
        // всех — то есть та, которую пользователь только что пересёк.
        if (top <= headerHeight && top > bestTop) {
          bestTop = top;
          active = cat.id;
        }
      }
      // Если ни одна ещё не доскроллилась (самый верх страницы) — первая.
      if (!active) active = sectionCategories[0]?.id || null;
      if (active) {
        setSelectedCategory((prev) => (prev === active ? prev : active));
      }
    };
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(run);
    };
    run();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [sectionCategories, data, selectedSection, normalizedQuery]);

  // Auto-scroll the horizontal category tabs to keep the active one centered
  useEffect(() => {
    if (!selectedCategory) return;
    const container = categoryScrollRef.current;
    if (!container) return;
    const btn = container.querySelector(`[data-category-btn-id="${selectedCategory}"]`);
    if (!btn) return;
    const target = btn.offsetLeft - container.clientWidth / 2 + btn.clientWidth / 2;
    container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [selectedCategory]);

  // Search helpers moved above

  const addToCart = (item) => {
    if (item.is_banner) return; // Don't add banners to cart
    
    const existing = cart.find(c => c.id === item.id);
    if (existing) {
      setCart(cart.map(c => c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
    toast.success(`${localizedName(item)} ${lang === 'en' ? 'added to cart' : 'добавлен в корзину'}`);
  };

  const updateCartQuantity = (itemId, delta) => {
    setCart(cart.map(c => {
      if (c.id === itemId) {
        const newQuantity = c.quantity + delta;
        return newQuantity > 0 ? { ...c, quantity: newQuantity } : null;
      }
      return c;
    }).filter(Boolean));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.id !== itemId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const isPreorder = data?.table?.is_preorder || false;
  const isDelivery = data?.table?.is_delivery || false;
  const isCartOnly = (data?.restaurant?.enabled_modules || []).includes('cart_only');
  // В режиме "корзина без заказа" кнопки "Добавить" должны быть видны,
  // даже если online_orders_enabled выключен — это и есть смысл модуля.
  const cartEnabled = (data?.settings?.online_orders_enabled ?? true) || isCartOnly;

  // Preorder form
  const [preorderForm, setPreorderForm] = useState({ name: '', phone: '', date: '', time: '' });
  const [deliveryForm, setDeliveryForm] = useState({ name: '', phone: '', city: '', address: '' });

  const submitOrder = async () => {
    if (cart.length === 0) return;

    if (isDelivery) {
      if (!deliveryForm.name.trim() || !deliveryForm.phone.trim()) {
        toast.error('Укажите имя и телефон'); return;
      }
      if (!deliveryForm.city.trim() || !deliveryForm.address.trim()) {
        toast.error('Укажите город и адрес доставки'); return;
      }
    } else if (isPreorder) {
      if (!preorderForm.name.trim() || !preorderForm.phone.trim()) {
        toast.error('Укажите имя и телефон для предзаказа');
        return;
      }
      if (!preorderForm.date) {
        toast.error('Укажите дату предзаказа');
        return;
      }
    }

    setSubmittingOrder(true);
    try {
      const orderTableCode = data?.table?.code || tableCode;
      const resp = await axios.post(`${API}/public/orders`, {
        table_code: orderTableCode,
        items: cart.map(item => ({
          menu_item_id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        notes: orderNotes,
        ...(isPreorder && {
          customer_name: preorderForm.name,
          customer_phone: preorderForm.phone,
          preorder_date: preorderForm.date,
          preorder_time: preorderForm.time,
        }),
        ...(isDelivery && {
          customer_name: deliveryForm.name,
          customer_phone: deliveryForm.phone,
          customer_city: deliveryForm.city,
          customer_address: deliveryForm.address,
        })
      });
      const orderData = { id: resp.data.id, status: 'new' };
      setActiveOrderId(orderData);
      setOrderStatus('new');
      localStorage.setItem(storageKey, JSON.stringify(orderData));
      setOrderSuccess(true);
      setCart([]);
      setOrderNotes('');
      setPreorderForm({ name: '', phone: '', date: '', time: '' });
      setDeliveryForm({ name: '', phone: '', city: '', address: '' });
      setTimeout(() => setOrderSuccess(false), 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка оформления заказа');
    } finally {
      setSubmittingOrder(false);
      setCartOpen(false);
    }
  };

  const callStaff = async (callTypeId) => {
    setCallingStaff(true);
    try {
      const callTableCode = data?.table?.code || tableCode;
      await axios.post(`${API}/public/staff-calls`, { 
        table_code: callTableCode,
        call_type_id: callTypeId 
      });
      const callType = data?.call_types?.find(ct => ct.id === callTypeId);
      toast.success(callType ? `${callType.name} - запрос отправлен` : 'Запрос отправлен');
      setCallModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка отправки запроса');
    } finally {
      setCallingStaff(false);
    }
  };

  const currency = data?.settings?.currency || 'BYN';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-mint-500/20 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-mint-500 animate-ping" />
          </div>
          <p className="text-muted-foreground font-medium">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <X className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground mb-2">{error}</h1>
          <p className="text-muted-foreground">Пожалуйста, обратитесь к персоналу ресторана</p>
        </div>
      </div>
    );
  }

  const { restaurant, settings, sections, table, call_types } = data;

  // Select random splash ad once when ads load
  if (!activeSplash && Array.isArray(data?.splash_ads) && data.splash_ads.length > 0) {
    const pick = data.splash_ads[Math.floor(Math.random() * data.splash_ads.length)];
    setActiveSplash(pick);
  }

  const splash = activeSplash;
  const showSplash = !splashDismissed && splash && (splash.image_url || splash.title || splash.text);
  const dismissSplash = () => {
    sessionStorage.setItem(`splash_${storageKey}`, '1');
    setSplashDismissed(true);
  };

  return (
    <div className="min-h-screen bg-background overflow-x-clip" data-testid="client-menu-page">
      {/* Splash / promo overlay */}
      {showSplash && (
        <div
          className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          data-testid="splash-overlay"
        >
          <div className="w-full max-w-lg bg-card rounded-3xl shadow-2xl overflow-hidden border border-border max-h-[92vh] flex flex-col">
            {splash.image_url && (
              <div className={`w-full bg-muted flex items-center justify-center ${splash.fit_mode === 'cover' ? 'aspect-[16/9]' : ''}`}>
                <img
                  src={splash.image_url}
                  alt="splash"
                  className={`w-full ${splash.fit_mode === 'cover' ? 'h-full object-cover' : 'h-auto object-contain max-h-[60vh]'}`}
                  data-testid="splash-image"
                />
              </div>
            )}
            <div className="p-6 space-y-4 text-center overflow-y-auto">
              {splash.title && (
                <h2 className="text-2xl font-heading font-bold text-foreground" data-testid="splash-title">
                  {splash.title}
                </h2>
              )}
              {splash.text && (
                <p className="text-sm text-muted-foreground whitespace-pre-line" data-testid="splash-text">
                  {splash.text}
                </p>
              )}
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  className="w-full h-12 rounded-2xl bg-mint-500 hover:bg-mint-600 text-white font-semibold"
                  onClick={dismissSplash}
                  data-testid="splash-continue-btn"
                >
                  {splash.button_text || 'Перейти к меню'}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                {splash.link_url && splash.link_text && (
                  <Button
                    variant="outline"
                    className="w-full h-12 rounded-2xl border-mint-500 text-mint-500 hover:bg-mint-50 dark:hover:bg-mint-900/20 font-semibold"
                    onClick={() => window.open(splash.link_url, '_blank', 'noopener,noreferrer')}
                    data-testid="splash-link-btn"
                  >
                    {splash.link_text}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header — sticky. На мобильной версии всегда в компактном режиме,
          на md+ разворачивается в полный размер. Без scroll-based schrink,
          чтобы не было моргания при колебаниях viewport (iOS Safari). */}
      <header
        ref={headerRef}
        className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border overflow-x-clip"
      >
        <div className="px-4 pt-2 pb-1.5 md:pt-4 md:pb-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {restaurant.logo_url ? (
                <img
                  src={restaurant.logo_url}
                  alt={restaurant.name}
                  className="rounded-xl object-cover flex-shrink-0 w-7 h-7 md:w-10 md:h-10"
                />
              ) : (
                <div className="rounded-xl bg-mint-500 flex items-center justify-center text-white font-bold flex-shrink-0 w-7 h-7 text-sm md:w-10 md:h-10 md:text-base">
                  {restaurant.name?.charAt(0)}
                </div>
              )}
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-foreground truncate text-sm leading-tight md:text-base md:leading-normal">
                  {restaurant.name}
                </h1>
                <p className="hidden md:block text-xs text-muted-foreground">{t('table_label')} №{table.number}</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => {
                  const next = !searchOpen;
                  setSearchOpen(next);
                  if (!next) setSearchQuery('');
                  if (next) setTimeout(() => searchInputRef.current?.focus(), 50);
                }}
                className={`rounded-full flex items-center justify-center w-8 h-8 md:w-9 md:h-9 ${
                  searchOpen
                    ? 'bg-mint-500 text-white'
                    : 'border border-border text-foreground/70 hover:bg-muted'
                }`}
                aria-label={t('search_placeholder')}
                data-testid="toggle-search-btn"
              >
                <Search className="w-4 h-4" />
              </button>
              <LanguageSwitcher
                lang={lang}
                setLang={setLang}
                availableLangs={enabledLangs}
                variant="icon"
                className="md:hidden"
              />
              {settings.staff_call_enabled && call_types && call_types.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden md:inline-flex rounded-full border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 px-3 whitespace-nowrap h-8 md:h-9"
                  onClick={() => setCallModalOpen(true)}
                  data-testid="call-staff-btn"
                >
                  <Bell className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t('call_waiter')}</span>
                </Button>
              )}
            </div>
          </div>

          {/* Language switcher + slogan — только desktop */}
          <div className="hidden md:flex justify-end mt-2">
            <LanguageSwitcher lang={lang} setLang={setLang} availableLangs={enabledLangs} />
          </div>

          {restaurant.slogan && (
            <p className="hidden md:block text-sm text-muted-foreground mt-2 italic">{restaurant.slogan}</p>
          )}
        </div>

        {/* Section tabs (Гастрономическое, Барное, Кальянное) */}
        <div className="px-4 pb-2.5">
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
            {sections.filter(s => s.is_active).map((section) => (
              <button
                key={section.id}
                onClick={() => setSelectedSection(section.id)}
                className={`px-3 py-1.5 sm:px-4 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                  selectedSection === section.id
                    ? 'bg-mint-500 text-white shadow-md shadow-mint-500/30'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                data-testid={`section-tab-${section.id}`}
              >
                {localizedName(section)}
              </button>
            ))}
          </div>
        </div>

        {/* Search input — collapsible */}
        {searchOpen && (
          <div className="px-4 pb-2.5" data-testid="search-panel">
            <div className="relative">
              <Search className="w-4 h-4 text-muted-foreground/70 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск по названию..."
                className="w-full h-10 pl-9 pr-9 rounded-xl bg-muted/70 border border-transparent focus:border-mint-500 focus:bg-background outline-none text-sm text-foreground placeholder:text-muted-foreground/70 transition-colors"
                data-testid="menu-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-muted flex items-center justify-center"
                  data-testid="menu-search-clear"
                  aria-label="Очистить поиск"
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Category tabs within section - improved mobile UX */}
        {visibleCategories.length > 0 && (
          <div className="border-t border-border/50">
            {/* Title + swipe hint */}
            <div className="flex items-center justify-between px-4 pt-2.5 pb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70" data-testid="category-section-title">
                Категория меню
              </span>
              {canScrollRight && showSwipeHint && (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground/60 animate-pulse" data-testid="swipe-hint">
                  <Hand className="w-3.5 h-3.5 swipe-hand-anim" />
                  <span>листайте</span>
                  <ChevronsRight className="w-3.5 h-3.5" />
                </span>
              )}
            </div>
            {/* Scrollable categories with arrows */}
            <div className="relative">
              {/* Left arrow */}
              {canScrollLeft && (
                <button
                  onClick={() => scrollCategories(-1)}
                  className="absolute left-0 top-0 bottom-0 z-10 w-9 flex items-center justify-center bg-gradient-to-r from-card via-card/80 to-transparent"
                  data-testid="scroll-categories-left"
                >
                  <ChevronsLeft className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              {/* Right arrow */}
              {canScrollRight && (
                <button
                  onClick={() => scrollCategories(1)}
                  className="absolute right-0 top-0 bottom-0 z-10 w-9 flex items-center justify-center bg-gradient-to-l from-card via-card/80 to-transparent"
                  data-testid="scroll-categories-right"
                >
                  <ChevronsRight className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              <div
                ref={categoryScrollRef}
                className="overflow-x-auto scrollbar-hide"
              >
                <div className="flex px-4 py-2 gap-2 min-w-max">
                  {visibleCategories.map((cat) => (
                    <button
                      key={cat.id}
                      data-category-btn-id={cat.id}
                      onClick={() => scrollToCategory(cat.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                        selectedCategory === cat.id
                          ? 'bg-brown-500 text-white'
                          : 'bg-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`category-tab-${cat.id}`}
                    >
                      {localizedName(cat)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Menu items */}
      <main className="px-4 py-6 pb-32">
        {/* Order status tracker */}
        {activeOrderId && orderStatus && (
          <div className="mb-6 p-4 rounded-xl bg-card border border-border shadow-sm" data-testid="order-tracker">
            <h3 className="font-heading font-semibold text-foreground mb-3">{t('order_status')}</h3>
            <div className="flex items-center gap-2">
              {['new', 'in_progress', 'completed'].map((step, idx) => {
                const isCancelled = orderStatus === 'cancelled';
                const isActive = step === orderStatus;
                const isPast = !isCancelled && (
                  (step === 'new' && ['in_progress', 'completed'].includes(orderStatus)) ||
                  (step === 'in_progress' && orderStatus === 'completed')
                );
                const labels = { new: t('order_status_pending'), in_progress: t('order_status_in_progress'), completed: t('order_status_completed') };
                const icons = { new: '📝', in_progress: '👨‍🍳', completed: '✅' };
                return (
                  <div key={step} className="flex items-center gap-2 flex-1">
                    <div className={`flex flex-col items-center gap-1 flex-1 ${isCancelled ? 'opacity-30' : ''}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all ${
                        isPast ? 'bg-green-500 text-white' :
                        isActive ? 'bg-mint-500 text-white animate-pulse' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {icons[step]}
                      </div>
                      <span className={`text-xs font-medium ${isActive ? 'text-mint-500' : isPast ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {labels[step]}
                      </span>
                    </div>
                    {idx < 2 && <div className={`h-0.5 flex-1 rounded ${isPast ? 'bg-green-500' : 'bg-muted'}`} />}
                  </div>
                );
              })}
            </div>
            {orderStatus === 'cancelled' && (
              <div className="mt-3 p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm text-center font-medium">
                Заказ отменён
              </div>
            )}
            {orderStatus === 'completed' && (
              <div className="mt-3 p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm text-center font-medium">
                Ваш заказ готов!
              </div>
            )}
            {(orderStatus === 'new' || orderStatus === 'in_progress') && (
              <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                Обновляется автоматически
              </div>
            )}
          </div>
        )}

        {orderSuccess && !activeOrderId && (
          <div className="mb-6 p-4 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800" data-testid="order-success-message">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-emerald-800 dark:text-emerald-200">
                  Заказ принят!
                </h3>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  Ваш заказ передан на кухню. Спасибо!
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-8" data-testid="menu-items-grid">
          {sectionCategories.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">В этом разделе пока нет категорий</p>
            </div>
          ) : normalizedQuery && totalMatches === 0 ? (
            <div className="text-center py-12" data-testid="menu-search-empty">
              <Search className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">{t('search_no_results')} «{searchQuery}»</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 rounded-full"
                onClick={() => setSearchQuery('')}
                data-testid="menu-search-empty-clear"
              >
                Сбросить поиск
              </Button>
            </div>
          ) : (
            sectionCategories.map((cat) => {
              const catItems = getCategoryItems(cat.id);
              if (catItems.length === 0) return null;
              const mode = cat.display_mode || 'card';
              return (
                <section
                  key={cat.id}
                  ref={(el) => { if (el) categoryRefs.current[cat.id] = el; else delete categoryRefs.current[cat.id]; }}
                  data-category-id={cat.id}
                  data-testid={`category-section-${cat.id}`}
                  className="scroll-mt-4"
                >
                  <h2 className="font-heading font-bold text-base text-foreground mb-3 px-1 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-mint-500" />
                    {localizedName(cat)}
                  </h2>

                  {mode === 'compact' ? (
                    <div className="bg-card rounded-2xl shadow-md overflow-hidden">
                      {catItems.map((item, index) => (
                        item.is_banner ? (
                          <div
                            key={item.id}
                            className={`p-4 ${index !== catItems.length - 1 ? 'border-b border-border' : ''}`}
                            data-testid={`banner-${item.id}`}
                          >
                            {item.image_url && (
                              <MenuImage src={item.image_url} alt={localizedName(item)} className="w-full h-auto rounded-lg mb-2" />
                            )}
                            {item.name && <h3 className="font-heading font-semibold text-foreground">{localizedName(item)}</h3>}
                            {item.description && <p className="text-sm text-muted-foreground">{localizedDescription(item)}</p>}
                          </div>
                        ) : (
                          <div
                            key={item.id}
                            className={`flex items-start justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-muted/40 transition-colors ${index !== catItems.length - 1 ? 'border-b border-border/50' : ''}`}
                            data-testid={`menu-item-${item.id}`}
                            onClick={() => setDetailsItem(item)}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2 flex-wrap">
                                <span className="font-medium text-foreground break-words">{localizedName(item)}</span>
                                {item.is_hit && <Star className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />}
                                {item.is_new && <Sparkles className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />}
                                {item.is_takeaway && <ShoppingBag className="w-3.5 h-3.5 text-sky-500 flex-shrink-0 mt-0.5" />}
                                {(item.label_ids || []).map(lid => {
                                  const lbl = labelsMap[lid];
                                  return lbl ? (
                                    <span key={lid} className="text-[10px] font-medium px-1.5 py-0 rounded-full text-white flex-shrink-0" style={{ backgroundColor: lbl.color }}>
                                      {lbl.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                              {item.description && (
                                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{localizedDescription(item)}</p>
                              )}
                              {item.weight && (
                                <span className="text-xs text-muted-foreground mt-0.5 inline-block">{item.weight}</span>
                              )}
                              <NutritionBadge item={item} variant="block" className="mt-0.5" />
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="font-bold text-mint-500 whitespace-nowrap">{item.price} {currency}</span>
                              {cartEnabled && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 rounded-full hover:bg-mint-100 dark:hover:bg-mint-900/30 p-0"
                                  onClick={(e) => { e.stopPropagation(); addToCart(item); }}
                                  data-testid={`add-to-cart-${item.id}`}
                                >
                                  <Plus className="w-4 h-4 text-mint-500" />
                                </Button>
                              )}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  ) : mode === 'tiles' ? (
                    <div className="grid grid-cols-2 gap-3">
                      {catItems.map((item) => (
                        item.is_banner ? (
                          <div
                            key={item.id}
                            className="col-span-2 rounded-2xl overflow-hidden shadow-md"
                            data-testid={`banner-${item.id}`}
                          >
                            {item.image_url && (
                              <MenuImage src={item.image_url} alt={localizedName(item)} className="w-full h-auto object-cover" />
                            )}
                            {(item.name || item.description) && (
                              <div className="p-3 bg-card">
                                {item.name && <h3 className="font-heading font-semibold text-foreground">{localizedName(item)}</h3>}
                                {item.description && <p className="text-sm text-muted-foreground mt-1">{localizedDescription(item)}</p>}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div
                            key={item.id}
                            className="bg-card rounded-2xl shadow-md overflow-hidden menu-item-tile cursor-pointer hover:shadow-lg transition-shadow flex flex-col"
                            data-testid={`menu-item-${item.id}`}
                            onClick={() => setDetailsItem(item)}
                          >
                            <div className="aspect-square w-full bg-black/40 relative">
                              {item.image_url ? (
                                <MenuImage src={item.image_url} alt={localizedName(item)} className="w-full h-full object-contain" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImageIcon className="w-10 h-10 text-muted-foreground/30" />
                                </div>
                              )}
                              <div className="absolute top-2 left-2 flex flex-col gap-1">
                                {item.is_hit && (
                                  <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 shadow">
                                    <Star className="w-3 h-3 mr-0.5" />Хит
                                  </Badge>
                                )}
                                {item.is_new && (
                                  <Badge className="bg-emerald-500 text-white text-[10px] px-1.5 py-0 shadow">
                                    <Sparkles className="w-3 h-3 mr-0.5" />Новинка
                                  </Badge>
                                )}
                                {item.is_spicy && (
                                  <Badge className="bg-orange-500 text-white text-[10px] px-1.5 py-0 shadow">
                                    <Flame className="w-3 h-3 mr-0.5" />Острое
                                  </Badge>
                                )}
                                {item.is_takeaway && (
                                  <Badge className="bg-sky-500 text-white text-[10px] px-1.5 py-0 shadow" data-testid={`badge-takeaway-${item.id}`}>
                                    <ShoppingBag className="w-3 h-3 mr-0.5" />На вынос
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="p-3 flex flex-col gap-2 flex-1">
                              <h3 className="font-heading font-semibold text-foreground text-sm leading-tight line-clamp-2">
                                {localizedName(item)}
                              </h3>
                              {item.description && (
                                <p className="text-xs text-muted-foreground line-clamp-3 leading-snug">
                                  {localizedDescription(item)}
                                </p>
                              )}
                              {(item.label_ids || []).length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {(item.label_ids || []).map(lid => {
                                    const lbl = labelsMap[lid];
                                    return lbl ? (
                                      <Badge key={lid} className="text-white text-[9px] px-1.5 py-0" style={{ backgroundColor: lbl.color }}>
                                        {lbl.name}
                                      </Badge>
                                    ) : null;
                                  })}
                                </div>
                              )}
                              <div className="mt-auto flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="font-bold text-mint-500 text-base whitespace-nowrap">{item.price} {currency}</div>
                                  {item.weight && (
                                    <div className="text-[11px] text-muted-foreground">{item.weight}</div>
                                  )}
                                  <NutritionBadge item={item} variant="block" />
                                </div>
                                {cartEnabled && (
                                  <Button
                                    size="sm"
                                    className="h-8 w-8 p-0 rounded-full bg-mint-500 hover:bg-mint-600 text-white shrink-0"
                                    onClick={(e) => { e.stopPropagation(); addToCart(item); }}
                                    data-testid={`add-to-cart-${item.id}`}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {catItems.map((item) => (
                        item.is_banner ? (
                          <div
                            key={item.id}
                            className="rounded-2xl overflow-hidden shadow-md"
                            data-testid={`banner-${item.id}`}
                          >
                            {item.image_url && (
                              <MenuImage src={item.image_url} alt={localizedName(item)} className="w-full h-auto object-cover" />
                            )}
                            {(item.name || item.description) && (
                              <div className="p-4 bg-card">
                                {item.name && (
                                  <h3 className="font-heading font-semibold text-foreground">{localizedName(item)}</h3>
                                )}
                                {item.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{localizedDescription(item)}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div
                            key={item.id}
                            className="bg-card rounded-2xl shadow-md overflow-hidden menu-item-card cursor-pointer hover:shadow-lg transition-shadow"
                            data-testid={`menu-item-${item.id}`}
                            onClick={() => setDetailsItem(item)}
                          >
                            <div className="flex">
                              <div className="w-28 h-28 flex-shrink-0 bg-black/40 relative">
                                {item.image_url ? (
                                  <MenuImage src={item.image_url} alt={localizedName(item)} className="w-full h-full object-contain" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <ImageIcon className="w-8 h-8 text-muted-foreground/30" />
                                  </div>
                                )}
                                <div className="absolute top-1 left-1 flex flex-col gap-1">
                                  {item.is_hit && (
                                    <Badge className="bg-red-500 text-white text-[9px] px-1 py-0 shadow">
                                      <Star className="w-2.5 h-2.5 mr-0.5" />Хит
                                    </Badge>
                                  )}
                                  {item.is_new && (
                                    <Badge className="bg-emerald-500 text-white text-[9px] px-1 py-0 shadow">
                                      <Sparkles className="w-2.5 h-2.5 mr-0.5" />Новинка
                                    </Badge>
                                  )}
                                  {item.is_spicy && (
                                    <Badge className="bg-orange-500 text-white text-[9px] px-1 py-0 shadow">
                                      <Flame className="w-2.5 h-2.5 mr-0.5" />Острое
                                    </Badge>
                                  )}
                                  {item.is_takeaway && (
                                    <Badge className="bg-sky-500 text-white text-[9px] px-1 py-0 shadow" data-testid={`badge-takeaway-${item.id}`}>
                                      <ShoppingBag className="w-2.5 h-2.5 mr-0.5" />На вынос
                                    </Badge>
                                  )}
                                </div>
                              </div>

                              <div className="flex-1 p-3 flex flex-col">
                                <div className="flex-1">
                                  <div className="flex items-start gap-1 flex-wrap mb-1">
                                    <h3 className="font-heading font-semibold text-foreground text-sm leading-tight">
                                      {localizedName(item)}
                                    </h3>
                                    {(item.label_ids || []).map(lid => {
                                      const lbl = labelsMap[lid];
                                      return lbl ? (
                                        <Badge key={lid} className="text-white text-[10px] px-1.5 py-0" style={{ backgroundColor: lbl.color }}>
                                          {lbl.name}
                                        </Badge>
                                      ) : null;
                                    })}
                                  </div>

                                  {item.description && (
                                    <p className="text-xs text-muted-foreground line-clamp-3 mb-2 leading-snug">
                                      {localizedDescription(item)}
                                    </p>
                                  )}
                                </div>

                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="font-bold text-mint-500">{item.price} {currency}</span>
                                    {item.weight && (
                                      <span className="text-xs text-muted-foreground ml-2">{item.weight}</span>
                                    )}
                                    <NutritionBadge item={item} variant="block" className="mt-0.5" />
                                  </div>

                                  {cartEnabled && (
                                    <Button
                                      size="sm"
                                      className="h-8 rounded-full bg-mint-500 hover:bg-mint-600 text-white px-3"
                                      onClick={(e) => { e.stopPropagation(); addToCart(item); }}
                                      data-testid={`add-to-cart-${item.id}`}
                                    >
                                      <Plus className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </section>
              );
            })
          )}
        </div>
      </main>

      {/* Restaurant info footer */}
      <div className="px-4 py-6 bg-muted/50 border-t border-border mb-20">
        <div className="space-y-3">
          {restaurant.address && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4" />
              <span>{restaurant.address}</span>
            </div>
          )}
          {restaurant.working_hours && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>{restaurant.working_hours}</span>
            </div>
          )}
          {restaurant.phone && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="w-4 h-4" />
              <a href={`tel:${restaurant.phone}`} className="hover:text-mint-500">
                {restaurant.phone}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Cart button */}
      {cartEnabled && cart.length > 0 && (
        <div className="fixed bottom-4 left-4 right-4 z-50" data-testid="cart-button-container">
          <Button
            className="w-full h-14 rounded-2xl bg-mint-500 hover:bg-mint-600 text-white shadow-lg shadow-mint-500/30"
            onClick={() => setCartOpen(true)}
            data-testid="open-cart-btn"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            <span className="font-semibold">{cartCount} поз.</span>
            <ChevronRight className="w-5 h-5 mx-2" />
            <span className="font-bold">{cartTotal} {currency}</span>
          </Button>
        </div>
      )}

      {/* Call Staff Modal */}
      <Dialog open={callModalOpen} onOpenChange={setCallModalOpen}>
        <DialogContent className="max-w-sm" data-testid="call-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-center">{t('call_choose_reason')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {call_types?.map((callType) => (
              <Button
                key={callType.id}
                variant="outline"
                className="w-full h-14 justify-start text-left rounded-xl hover:bg-mint-50 hover:border-mint-500 dark:hover:bg-mint-900/20"
                onClick={() => callStaff(callType.id)}
                disabled={callingStaff}
                data-testid={`call-type-${callType.id}`}
              >
                <Bell className="w-5 h-5 mr-3 text-mint-500" />
                <span className="font-medium">{localizedName(callType)}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cart Dialog */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col" data-testid="cart-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {isCartOnly ? t('cart_show_waiter') : t('cart_title')}
            </DialogTitle>
            {isCartOnly && data?.table?.number && (
              <p className="text-sm text-muted-foreground" data-testid="cart-only-table-info">
                {t('table_label')} №<span className="font-semibold text-foreground">{data.table.number}</span>
              </p>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {cart.map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50" data-testid={`cart-item-${item.id}`}>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground text-sm">{localizedName(item)}</h4>
                  <p className="text-sm text-mint-500 font-semibold">{item.price} {currency}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => updateCartQuantity(item.id, -1)}
                    data-testid={`decrease-${item.id}`}
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <span className="w-6 text-center font-semibold">{item.quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => updateCartQuantity(item.id, 1)}
                    data-testid={`increase-${item.id}`}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => removeFromCart(item.id)}
                    data-testid={`remove-${item.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}

            <div className="pt-3 space-y-3">
              {!isCartOnly && isPreorder && (
                <div className="space-y-3 p-3 rounded-xl bg-purple-500/5 border border-purple-500/20">
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Данные для предзаказа</p>
                  <input
                    type="text"
                    placeholder="Ваше имя *"
                    value={preorderForm.name}
                    onChange={(e) => setPreorderForm({...preorderForm, name: e.target.value})}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-sm"
                    data-testid="preorder-name"
                  />
                  <input
                    type="tel"
                    placeholder="Телефон *"
                    value={preorderForm.phone}
                    onChange={(e) => setPreorderForm({...preorderForm, phone: e.target.value})}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-sm"
                    data-testid="preorder-phone"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={preorderForm.date}
                      onChange={(e) => setPreorderForm({...preorderForm, date: e.target.value})}
                      className="p-2.5 rounded-lg border border-border bg-background text-sm"
                      data-testid="preorder-date"
                    />
                    <input
                      type="time"
                      value={preorderForm.time}
                      onChange={(e) => setPreorderForm({...preorderForm, time: e.target.value})}
                      className="p-2.5 rounded-lg border border-border bg-background text-sm"
                      data-testid="preorder-time"
                    />
                  </div>
                </div>
              )}
              {!isCartOnly && isDelivery && (
                <div className="space-y-3 p-3 rounded-xl bg-orange-500/5 border border-orange-500/20">
                  <p className="text-sm font-medium text-orange-600 dark:text-orange-400">🚚 Данные для доставки</p>
                  <input
                    type="text"
                    placeholder="Ваше имя *"
                    value={deliveryForm.name}
                    onChange={(e) => setDeliveryForm({ ...deliveryForm, name: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-sm"
                    data-testid="delivery-name"
                  />
                  <input
                    type="tel"
                    placeholder="Телефон *"
                    value={deliveryForm.phone}
                    onChange={(e) => setDeliveryForm({ ...deliveryForm, phone: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-sm"
                    data-testid="delivery-phone"
                  />
                  <input
                    type="text"
                    placeholder="Город *"
                    value={deliveryForm.city}
                    onChange={(e) => setDeliveryForm({ ...deliveryForm, city: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-sm"
                    data-testid="delivery-city"
                  />
                  <input
                    type="text"
                    placeholder="Улица, дом, квартира, подъезд *"
                    value={deliveryForm.address}
                    onChange={(e) => setDeliveryForm({ ...deliveryForm, address: e.target.value })}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-sm"
                    data-testid="delivery-address"
                  />
                </div>
              )}
              {!isCartOnly && (
                <Textarea
                  placeholder={t('order_notes_placeholder')}
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  rows={2}
                  className="resize-none"
                  data-testid="order-notes-input"
                />
              )}
              {isCartOnly && (
                <div className="rounded-xl bg-mint-500/10 border border-mint-500/30 p-4 text-center">
                  <Hand className="w-8 h-8 text-mint-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-foreground">{t('cart_show_waiter')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {lang === 'en'
                      ? "It's just a draft — nothing was sent to the kitchen. Place it in person with the waiter."
                      : 'Это просто черновик — он не отправлен на кухню. Оформите его лично у официанта.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-lg font-heading font-semibold">{t('cart_subtotal')}:</span>
              <span className="text-2xl font-bold text-mint-500">{cartTotal} {currency}</span>
            </div>

            {isCartOnly ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 h-12 rounded-xl"
                  onClick={() => { setCart([]); toast.success(t('cart_empty')); }}
                  disabled={cart.length === 0}
                  data-testid="clear-cart-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('cart_clear')}
                </Button>
                <Button
                  className="flex-1 h-12 rounded-xl bg-mint-500 hover:bg-mint-600 text-white font-semibold"
                  onClick={() => setCartOpen(false)}
                  data-testid="cart-only-close-btn"
                >
                  <Check className="w-5 h-5 mr-2" />
                  {t('cart_done')}
                </Button>
              </div>
            ) : (
              <Button
                className="w-full h-12 rounded-xl bg-mint-500 hover:bg-mint-600 text-white font-semibold"
                onClick={submitOrder}
                disabled={submittingOrder || cart.length === 0}
                data-testid="submit-order-btn"
              >
                {submittingOrder ? (
                  <>
                    <div className="spinner mr-2" />
                    {t('submitting')}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {t('submit_order')}
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Toaster position="top-center" richColors />

      {/* Mobile-only floating «Вызвать» — крупная амбер pill в левом нижнем углу */}
      {settings.staff_call_enabled && call_types && call_types.length > 0 && (
        <button
          onClick={() => setCallModalOpen(true)}
          aria-label={t('call_waiter')}
          data-testid="call-staff-fab"
          className={`md:hidden fixed left-1/2 -translate-x-1/2 z-40 h-12 px-6 rounded-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-semibold shadow-lg shadow-amber-500/30 flex items-center gap-2 transition-all ${cartEnabled && cart.length > 0 ? 'bottom-24' : 'bottom-4'}`}
        >
          <Bell className="w-5 h-5 flex-shrink-0" />
          <span className="leading-[1.05] text-[15px]">{t('call_waiter')}</span>
        </button>
      )}

      {/* Scroll to top — floating button, появляется после 400px скролла */}
      {showTopBtn && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Наверх"
          data-testid="scroll-to-top-btn"
          className={`fixed right-4 z-40 w-11 h-11 rounded-full bg-mint-500 hover:bg-mint-600 text-white shadow-lg flex items-center justify-center transition-all ${cartEnabled && cart.length > 0 ? 'bottom-24' : 'bottom-4'}`}
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}

      <ItemDetailsDialog
        open={!!detailsItem}
        onOpenChange={(o) => { if (!o) setDetailsItem(null); }}
        item={detailsItem}
        currency={currency}
        labelsMap={labelsMap}
        ordersEnabled={cartEnabled}
        onAdd={addToCart}
      />
    </div>
  );
}
